import {useCallback} from 'react';

import {useDraft} from '../composer';
import type {Thread} from '../store';
import {queueKey, useQueueStore} from './store';

/**
 * useQueueRecall pulls a queued message back into the active thread's composer
 * draft for editing. `recall` prefills the draft from the item's parts,
 * restoring the ambient run chips as editable chips, and removes it from the
 * queue, so re-sending re-queues it. Returns whether it recalled; a refused
 * prefill (an occupied draft) or a missing item leaves the queue intact.
 */
export function useQueueRecall(
  activeThread: Thread | undefined
): (id: string) => boolean {
  const draft = useDraft(activeThread?.id ?? null);

  return useCallback(
    (id: string): boolean => {
      if (activeThread == null) {
        return false;
      }

      const key = queueKey(activeThread.id);
      const item = useQueueStore.getState().queues[key]?.find(m => m.id === id);
      if (item == null) {
        return false;
      }

      if (!draft.setParts(item.content)) {
        return false;
      }

      useQueueStore.getState().remove(key, id);
      return true;
    },
    [activeThread, draft]
  );
}
